// Retrieval core (Phase 1). The ACCURACY-LOCK lives here: every value the UI
// renders originates from a real Sefaria response validated THIS request. We
// fail loud on shape drift (never render partial/garbage), cap concurrency, back
// off on 429/5xx, and bound per-session requests. Public REST API, CORS *.
// No backend. App code may use Date.now() / setTimeout freely.

const BASE = "https://www.sefaria.org/api";
const MAX_CONCURRENT = 5;
const MAX_RETRIES = 3;
const SESSION_BUDGET = 800; // hard cap on total requests this session (RISK-2)
const TIMEOUT_MS = 15000;

export class SefariaError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = "SefariaError";
    this.status = status;
  }
}

// ---- public types -----------------------------------------------------------

export interface ParashaWeek {
  ref: string; // e.g. "Numbers 13:1-15:41"
  parasha: string; // displayValue.en, e.g. "Sh'lach"
  parashaHe: string; // displayValue.he
}

export interface VerifiedText {
  ref: string;
  he: string;
  en: string | null;
  heVersionTitle: string | null; // version-pinning provenance (ACC-2)
  enVersionTitle: string | null;
  versionSource: string | null;
  // Sefaria's own Hebrew reference label + primary category for THIS ref, when the
  // response carried them (real field or null). Used by the quote-recovery layer to
  // label + classify a recovered source without a second request - never invented.
  heRef?: string | null;
  primaryCategory?: string | null;
}

export interface Verse {
  ref: string; // "Numbers 15:38"
  he: string;
  en: string;
}

export interface ParashaText {
  ref: string;
  verses: Verse[];
  heVersionTitle: string | null;
  enVersionTitle: string | null;
}

// A real linked source returned THIS request (the hyperlink the navigator follows).
export interface SourceLink {
  ref: string; // "Rashi on Numbers 15:38:1"
  category: string; // "Commentary", "Targum", "Midrash", ...
  commentator: string; // collectiveTitle.en || index_title
  commentatorHe: string | null;
  anchorRef: string; // the verse this link hangs off, "Numbers 15:38"
  linkType: string; // Sefaria link `type` ("commentary"/"targum"/"quotation"/...; "" when absent). Drives the honest relationship label. Real field only - never invented.
  // Language availability of the LINKED text, validated THIS request:
  // - hasEn: Sefaria's per-link `sourceHasEn` boolean (present even at
  //   with_text=0; measured live 2026-06-09 it never over-promises, though it
  //   under-reports a small tail - sourceLang.ts carries curated overrides).
  //   null = the field was absent/non-boolean -> availability UNKNOWN, and the
  //   display-mode filter must show the source (accuracy-lock: never hide on
  //   unvalidated data). with_text=1 (5x payload) is the documented upgrade
  //   path if per-segment text presence is ever needed at render time.
  // - hasHe: reserved; the links API exposes no `sourceHasHe` (verified), so
  //   this is always null today. The corpus is Hebrew-first and getText fail-
  //   louds without Hebrew text.
  hasEn: boolean | null;
  hasHe: boolean | null;
  // For RANGE-anchored links ("Genesis 1:1-3"): the covered verse refs as
  // Sefaria returned them (anchorRefExpanded). null when absent/invalid. Used
  // by the chapter-batched scent layer to distribute a range to its verses;
  // never invented (real field or null).
  anchorRefExpanded: string[] | null;
  // Sefaria's per-link `compDate` (the WORK's composition date) - present on the
  // links payload at with_text=0 (verified live 2026-06-22: "Ramban on Numbers
  // 19:2" -> [1246, 1270]). [year] or [start, end], CE (negative = BCE). null when
  // absent/malformed (accuracy-lock: a real array of 1-2 finite numbers or null,
  // never invented). Feeds the era / time-hop layer (timeline.ts eraOf), no extra fetch.
  compDate: [number] | [number, number] | null;
  // Sefaria index `era` code ("T"/"A"/"GN"/"RI"/"AH"/"CO") when the link carries it,
  // else null (the links payload usually omits it; compDate is the load-bearing field).
  era: string | null;
}

// ---- throttled / retrying / budgeted fetch ----------------------------------

// Deliberate module-level singletons: this module IS the one Sefaria client for
// the app (one backend, one SPA). active/spent/waiters back the shared concurrency
// gate + hard session budget; _resetCaches() restores them between tests.
let active = 0;
let spent = 0;
const waiters: Array<() => void> = [];

function acquire(): Promise<void> {
  if (active < MAX_CONCURRENT) {
    active++;
    return Promise.resolve();
  }
  return new Promise((resolve) => {
    waiters.push(() => {
      active++;
      resolve();
    });
  });
}

function release(): void {
  active--;
  const next = waiters.shift();
  if (next) next();
}

const retriable = (status: number): boolean => status === 429 || status >= 500;
const sleep = (ms: number): Promise<void> => new Promise((r) => setTimeout(r, ms));
// exponential backoff with jitter: ~0.4s, 0.8s, 1.6s (+/- up to 250ms)
const backoff = (attempt: number): number => 400 * 2 ** attempt + Math.floor(Math.random() * 250);

async function fetchJson(path: string): Promise<unknown> {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), TIMEOUT_MS);
  try {
    const res = await fetch(`${BASE}${path}`, {
      signal: ctrl.signal,
      headers: { Accept: "application/json" },
    });
    // retriable (429/5xx) and every other non-ok status throw the same error here;
    // sefariaFetch decides retry-vs-fail downstream by inspecting e.status (retriable).
    if (!res.ok) throw new SefariaError(`HTTP ${res.status}`, res.status);
    return (await res.json()) as unknown;
  } finally {
    clearTimeout(timer);
  }
}

function reserveCoreRequest(): void {
  if (spent >= SESSION_BUDGET) {
    throw new SefariaError(`Sefaria request budget (${SESSION_BUDGET}) exhausted this session`, 0);
  }
  spent++; // reserve synchronously, before any await, so the cap is truly hard
}

async function withSefariaSlot<T>(fn: () => Promise<T>): Promise<T> {
  await acquire();
  try {
    return await fn();
  } finally {
    release();
  }
}

// Generic fetch+validate. Shape mismatch and 4xx are non-retriable (fail loud);
// network errors and 429/5xx retry with backoff. Each physical request reserves
// budget, so retries cannot exceed the session cap.
async function sefariaFetch<T>(path: string, validate: (d: unknown) => d is T): Promise<T> {
  let lastErr: unknown;
  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    reserveCoreRequest();
    try {
      const data = await withSefariaSlot(() => fetchJson(path));
      if (!validate(data)) {
        throw new SefariaError(`Unexpected Sefaria response shape for ${path}`, 0);
      }
      return data;
    } catch (e) {
      lastErr = e;
      const nonRetriable = e instanceof SefariaError && !retriable(e.status);
      if (nonRetriable || attempt === MAX_RETRIES) break;
      await sleep(backoff(attempt));
    }
  }
  throw lastErr instanceof Error ? lastErr : new SefariaError(`fetch failed for ${path}`, 0);
}

// Enhancement-only fetch+validate: guarded by the caller's feature cap, but it
// does not spend the core reading budget.
async function sefariaEnhancementFetch<T>(path: string, validate: (d: unknown) => d is T): Promise<T> {
  const data = await withSefariaSlot(() => fetchJson(path));
  if (!validate(data)) {
    throw new SefariaError(`Unexpected Sefaria response shape for ${path}`, 0);
  }
  return data;
}

async function fetchVerifiedText(
  ref: string,
  load: (path: string, validate: (d: unknown) => d is Record<string, unknown>) => Promise<Record<string, unknown>>,
): Promise<VerifiedText> {
  const enc = encodeURIComponent(ref);
  const d = await load(`/texts/${enc}?context=0&pad=0&commentary=0`, isTextResp);
  const he = flattenText(d.he);
  const en = stripTranslatorFootnotes(flattenText(d.text));
  if (!he) throw new SefariaError(`No Hebrew text returned for ${ref}`, 0);
  return {
    ref: typeof d.ref === "string" ? d.ref : ref,
    he,
    en: en || null,
    heVersionTitle: typeof d.heVersionTitle === "string" ? d.heVersionTitle : null,
    enVersionTitle: typeof d.versionTitle === "string" ? d.versionTitle : null,
    versionSource: typeof d.versionSource === "string" ? d.versionSource : null,
    heRef: typeof d.heRef === "string" ? d.heRef : null,
    primaryCategory: typeof d.primary_category === "string" ? d.primary_category : null,
  };
}

// ---- small shape guards ------------------------------------------------------

function isObj(d: unknown): d is Record<string, unknown> {
  return typeof d === "object" && d !== null;
}

// flatten Sefaria's string | nested-array text into a single trimmed string.
function flattenText(v: unknown): string {
  if (typeof v === "string") return v;
  if (Array.isArray(v)) return v.map(flattenText).join(" ");
  return "";
}

// Audit 2026-06-12: decoded Genesis 1:1 and Numbers 15:38 Hebrew had no footnote classes.
export function stripTranslatorFootnotes(html: string): string {
  if (!html.includes("footnote")) return html;
  const template = document.createElement("template");
  template.innerHTML = html;
  const footnotes = template.content.querySelectorAll("sup.footnote-marker, i.footnote");
  if (!footnotes.length) return html;
  footnotes.forEach((node) => node.remove());
  return template.innerHTML;
}

// ---- endpoints ---------------------------------------------------------------

interface CalendarsResp {
  calendar_items: Array<Record<string, unknown>>;
}
const isCalendars = (d: unknown): d is CalendarsResp =>
  isObj(d) && Array.isArray((d as { calendar_items?: unknown }).calendar_items);

// Resolve which parasha is current ONLINE-FIRST (never trust cache). Diaspora
// default for US (RISK-3). Returns the combined ref + display names.
export async function getThisWeekParasha(diaspora = true): Promise<ParashaWeek> {
  const d = await sefariaFetch(`/calendars?diaspora=${diaspora ? 1 : 0}`, isCalendars);
  const item = d.calendar_items.find(
    (i) => isObj(i) && isObj(i.title) && (i.title as { en?: unknown }).en === "Parashat Hashavua",
  );
  if (!item) throw new SefariaError("No Parashat Hashavua in calendars response", 0);
  const ref = item.ref;
  const display = isObj(item.displayValue) ? item.displayValue : {};
  if (typeof ref !== "string") throw new SefariaError("Parasha item missing a ref", 0);
  return {
    ref,
    parasha: typeof (display as { en?: unknown }).en === "string" ? ((display as { en: string }).en) : ref,
    parashaHe: typeof (display as { he?: unknown }).he === "string" ? ((display as { he: string }).he) : "",
  };
}

const isTextResp = (d: unknown): d is Record<string, unknown> =>
  isObj(d) && ("he" in d || "text" in d) && "ref" in d;

// Per-session in-memory caches (perf): a source's text and its links are
// deterministic, so the same ref opened under different verses/branches - or the
// auto-loaded "Cited here" prefetch - costs one request, not many. This also
// relieves the per-session request budget. Memory only; never persisted (the
// "which parasha" calendar lookup stays online-first elsewhere, accuracy-lock).
const textCache = new Map<string, VerifiedText>();
// Single-flight: concurrent callers for the SAME ref join one request instead of
// each firing (and double-charging the budget). Recovery fans getText out
// concurrently over an overlapping ref set, so this collapses the duplicates the
// way searchRefs/findRefsInText already do. Failures are NEVER cached (fail-loud).
const textInflight = new Map<string, Promise<VerifiedText>>();
const linksCache = new Map<string, SourceLink[]>();

// One ref (single verse or range) at its current pinned version, with provenance.
export async function getText(ref: string): Promise<VerifiedText> {
  const hit = textCache.get(ref);
  if (hit) return hit;
  const running = textInflight.get(ref);
  if (running) return running; // join the in-flight fetch (no second request/budget charge)
  const run = (async (): Promise<VerifiedText> => {
    const result = await fetchVerifiedText(ref, sefariaFetch);
    textCache.set(ref, result);
    return result;
  })();
  textInflight.set(ref, run);
  try {
    return await run;
  } finally {
    textInflight.delete(ref);
  }
}

// Quote-recovery's candidate-text fetch, on its OWN session allowance so it can
// never drain the shared SESSION_BUDGET that core reading (getText/getLinks)
// depends on. Cache HITS are free (don't count); only real fetches (cache misses)
// count, and once the cap is reached recovery gets null (skip the candidate) -
// it NEVER throws, so a budget-heavy session degrades recovery gracefully while
// the next verse/source the learner opens always still loads.
let recoverTextSpent = 0;
const RECOVER_TEXT_CAP = 220;
export async function getTextForRecovery(ref: string): Promise<VerifiedText | null> {
  const hit = textCache.get(ref);
  if (hit) return hit; // already fetched (by core reading or an earlier candidate) - free
  if (recoverTextSpent >= RECOVER_TEXT_CAP) return null;
  recoverTextSpent++;
  try {
    const result = await fetchVerifiedText(ref, sefariaEnhancementFetch);
    textCache.set(ref, result);
    return result;
  } catch {
    return null; // recovery is an enhancement - a failed candidate fetch is just skipped
  }
}

interface ParsedRange {
  book: string;
  startCh: number;
  startV: number;
  endCh: number;
  endV: number;
}

// "Numbers 13:1-15:41" -> structured. Also handles single-chapter "Book c:v-c:v".
export function parseParashaRef(ref: string): ParsedRange {
  const m = ref.match(/^(.+?) (\d+):(\d+)-(\d+):(\d+)$/);
  if (m) {
    return { book: m[1], startCh: +m[2], startV: +m[3], endCh: +m[4], endV: +m[5] };
  }
  // "Book c:v-v" (same-chapter range) or "Book c:v" (single verse) fallbacks.
  const m2 = ref.match(/^(.+?) (\d+):(\d+)(?:-(\d+))?$/);
  if (m2) {
    const ch = +m2[2];
    return { book: m2[1], startCh: ch, startV: +m2[3], endCh: ch, endV: m2[4] ? +m2[4] : +m2[3] };
  }
  throw new SefariaError(`Cannot parse parasha ref "${ref}"`, 0);
}

// Fetch a parasha's verses chapter-by-chapter (a few requests, avoids the
// spanning-array parsing). Each verse gets its own stable individual ref so
// per-verse state (kept/explored/help-level) keys correctly (ACC-4).
export async function getParashaVerses(parashaRef: string): Promise<ParashaText> {
  const { book, startCh, startV, endCh, endV } = parseParashaRef(parashaRef);

  // Fetch every chapter CONCURRENTLY (perf: the verses are the LCP content - a
  // multi-chapter parasha used to pay one sequential round-trip PER chapter).
  // The acquire/release semaphore still bounds in-flight requests to
  // MAX_CONCURRENT, so this is a burst-safe sum-of-RTT -> max-of-RTT win.
  // Promise.all preserves INPUT order, so `responses[i]` is always chapter
  // `chapterNums[i]` regardless of network settle order - the assembly below
  // stays byte-identical to the old sequential loop (verse order AND first-non-
  // null version-title selection, walked in ascending chapter order). One failed
  // chapter rejects the whole fetch - same fail-loud posture as before (ACCURACY-
  // LOCK: never render partial; the reader shows its error/retry).
  const chapterNums: number[] = [];
  for (let ch = startCh; ch <= endCh; ch++) chapterNums.push(ch);
  const responses = await Promise.all(
    chapterNums.map((ch) =>
      sefariaFetch(`/texts/${encodeURIComponent(`${book}.${ch}`)}?context=0&pad=0&commentary=0`, isTextResp),
    ),
  );

  const verses: Verse[] = [];
  let heVersionTitle: string | null = null;
  let enVersionTitle: string | null = null;
  for (let i = 0; i < responses.length; i++) {
    const ch = chapterNums[i];
    const d = responses[i];
    if (heVersionTitle === null && typeof d.heVersionTitle === "string") heVersionTitle = d.heVersionTitle;
    if (enVersionTitle === null && typeof d.versionTitle === "string") enVersionTitle = d.versionTitle;

    const heArr = Array.isArray(d.he) ? d.he : [d.he];
    const enArr = Array.isArray(d.text) ? d.text : [d.text];
    const from = ch === startCh ? startV : 1;
    const to = ch === endCh ? endV : heArr.length;
    for (let v = from; v <= to; v++) {
      const he = flattenText(heArr[v - 1]);
      if (!he) throw new SefariaError(`Missing Hebrew text for ${book} ${ch}:${v}`, 0);
      verses.push({ ref: `${book} ${ch}:${v}`, he, en: stripTranslatorFootnotes(flattenText(enArr[v - 1])) });
    }
  }
  if (!verses.length) throw new SefariaError(`No verses resolved for ${parashaRef}`, 0);
  return { ref: parashaRef, verses, heVersionTitle, enVersionTitle };
}

// Fail loud on shape drift: a non-empty links array must hold objects with a
// string `ref` and `category` (accuracy-lock). Empty array is legitimate (a ref
// with no links).
const isLinksResp = (d: unknown): d is Array<Record<string, unknown>> =>
  Array.isArray(d) &&
  d.every((row) => isObj(row) && typeof row.ref === "string" && typeof row.category === "string");

// Validate Sefaria's per-link `compDate`: a real array of 1-2 finite numbers, else
// null. Accuracy-lock - a malformed/absent value yields null (UNKNOWN era downstream),
// never a guessed year.
export function validCompDate(v: unknown): [number] | [number, number] | null {
  if (!Array.isArray(v) || v.length < 1 || v.length > 2) return null;
  if (!v.every((n) => typeof n === "number" && Number.isFinite(n))) return null;
  return v.length === 1 ? [v[0] as number] : [v[0] as number, v[1] as number];
}

// Map one raw links-API row to a SourceLink (shared by the per-ref fetch and
// the chapter-batched scent fetch - ONE validation path, never two).
function mapLinkRow(l: Record<string, unknown>, fallbackAnchor: string): SourceLink | null {
  const linkRef = l.ref;
  const category = l.category;
  if (typeof linkRef !== "string" || typeof category !== "string") return null;
  const collective = isObj(l.collectiveTitle) ? l.collectiveTitle : {};
  const commentator =
    typeof (collective as { en?: unknown }).en === "string"
      ? (collective as { en: string }).en
      : typeof l.index_title === "string"
        ? l.index_title
        : linkRef;
  const expandedRaw = l.anchorRefExpanded;
  const anchorRefExpanded =
    Array.isArray(expandedRaw) && expandedRaw.length && expandedRaw.every((r) => typeof r === "string")
      ? (expandedRaw as string[])
      : null;
  return {
    ref: linkRef,
    category,
    commentator,
    commentatorHe:
      typeof (collective as { he?: unknown }).he === "string" ? (collective as { he: string }).he : null,
    anchorRef: typeof l.anchorRef === "string" ? l.anchorRef : fallbackAnchor,
    linkType: typeof l.type === "string" ? l.type : "",
    hasEn: typeof l.sourceHasEn === "boolean" ? l.sourceHasEn : null,
    hasHe: null, // no provider field exists (see SourceLink) - never guessed
    anchorRefExpanded,
    compDate: validCompDate(l.compDate),
    era: typeof l.era === "string" ? l.era : null,
  };
}

// Single-flight for getLinks, mirroring textInflight: concurrent callers for the
// SAME ref join one request (one budget charge). Failures are NEVER cached.
const linksInflight = new Map<string, Promise<SourceLink[]>>();

// The hyperlinks the navigator follows: every real source linked to `ref`.
export async function getLinks(ref: string): Promise<SourceLink[]> {
  const hit = linksCache.get(ref);
  if (hit) return hit;
  const running = linksInflight.get(ref);
  if (running) return running; // join the in-flight fetch (no second request/budget charge)
  const run = (async (): Promise<SourceLink[]> => {
    const enc = encodeURIComponent(ref);
    const d = await sefariaFetch(`/links/${enc}?with_text=0`, isLinksResp);
    const out: SourceLink[] = [];
    for (const l of d) {
      if (!isObj(l)) continue;
      const link = mapLinkRow(l, ref);
      if (link) out.push(link);
    }
    linksCache.set(ref, out);
    return out;
  })();
  linksInflight.set(ref, run);
  try {
    return await run;
  } finally {
    linksInflight.delete(ref);
  }
}

// Chapter-batched links for the scent layer ("Numbers 13" -> every link in the
// chapter, verse-granular anchorRef; spiked live 2026-06-10: ~130-470 KB gzip
// per chapter, 97% verse-granular + anchorRefExpanded on ranges). DELIBERATELY
// UNCACHED: the caller reduces to per-verse tiers and discards - a Genesis 1
// payload is ~11k links and must never be retained in linksCache.
export async function getChapterLinks(chapterRef: string): Promise<SourceLink[]> {
  const enc = encodeURIComponent(chapterRef);
  const d = await sefariaFetch(`/links/${enc}?with_text=0`, isLinksResp);
  const out: SourceLink[] = [];
  for (const l of d) {
    if (!isObj(l)) continue;
    const link = mapLinkRow(l, chapterRef);
    if (link) out.push(link);
  }
  return out;
}

// ---- find-refs (inline citation resolution) ----------------------------------
// Sefaria's linker: resolves free-text citations ("cf. Exod. 15.17") inside a
// submitted text body into canonical refs WITH character spans. POST returns a
// task id; the result arrives via GET /async/{id} (measured live 2026-06-10:
// ready in ~0.3s). This is a reading ENHANCEMENT, so the accuracy-lock posture
// here is validate-or-skip: ANY shape drift anywhere makes the whole call
// return null (cached), the caller renders nothing extra, and reading is never
// blocked. No retries, no backoff - one POST + a bounded poll, every request
// counted against the session budget and the concurrency cap.

export interface FoundRef {
  startChar: number; // char offsets into the EXACT body string submitted
  endChar: number;
  text: string; // the matched span, verbatim
  refs: string[]; // canonical refs Sefaria resolved ([] when none)
  linkFailed: boolean; // Sefaria could not resolve this span - caller skips
}

export interface FindRefsResult {
  results: FoundRef[];
  refData: Record<string, { heRef: string; primaryCategory: string }>;
}

const findRefsCache = new Map<string, FindRefsResult | null>();
// In-flight requests, keyed like the result cache. Concurrent callers for the
// SAME source (the hidden engine text AND the wide reader's projection of it)
// share ONE request, so find-refs is spent at most once per source - never
// raced into N parallel POSTs before the result-cache is populated.
const findRefsInflight = new Map<string, Promise<FindRefsResult | null>>();
// Poll sleeps (~8s total deadline). The task is typically ready in ~300ms
// (measured live), so the FIRST poll at 400ms usually lands it in one.
const FINDREFS_POLL_MS = [400, 600, 1000, 1500, 2000, 2500];
// The enhancement gets its OWN request allowance: a decorative feature must
// never drain the shared SESSION_BUDGET that core reading (getText/getLinks)
// depends on. Capped well below the main budget; once spent, pills simply
// stop appearing for the session - reading is untouched.
let findRefsSpent = 0;
const FINDREFS_SESSION_CAP = 120;

// POST variant of fetchJson (which is GET-only). Same timeout + abort plumbing.
// opts.simple omits the `Content-Type: application/json` header so the POST stays
// a CORS "simple request" (no OPTIONS preflight). Required for /search-wrapper:
// unlike /find-refs, Sefaria does NOT answer the preflight for that endpoint, so
// a JSON content-type makes the browser block the POST ("Failed to fetch") - even
// though the host is CSP-allowed. The server parses the JSON body regardless of
// content-type (verified live). Server-side test runners don't preflight, so this
// only ever bites in a real browser.
async function postJson(path: string, payload: unknown, opts: { simple?: boolean } = {}): Promise<unknown> {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), TIMEOUT_MS);
  try {
    const headers: Record<string, string> = { Accept: "application/json" };
    if (!opts.simple) headers["Content-Type"] = "application/json";
    const res = await fetch(`${BASE}${path}`, {
      method: "POST",
      signal: ctrl.signal,
      headers,
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new SefariaError(`HTTP ${res.status}`, res.status);
    return (await res.json()) as unknown;
  } finally {
    clearTimeout(timer);
  }
}

// One budgeted, slot-capped, UNRETRIED request (the enhancement never retries;
// slots are held per request, not across the poll loop, so a slow task queue
// cannot starve the reading fetches).
async function oneShot(fn: () => Promise<unknown>): Promise<unknown> {
  return withSefariaSlot(fn);
}

function parseFindRefs(d: Record<string, unknown>): FindRefsResult | null {
  const result = d.result;
  if (!isObj(result)) return null;
  const body = result.body;
  if (!isObj(body)) return null;
  const rows = body.results;
  const refDataRaw = body.refData;
  if (!Array.isArray(rows) || !isObj(refDataRaw)) return null;
  const results: FoundRef[] = [];
  for (const r of rows) {
    if (!isObj(r)) return null;
    if (typeof r.startChar !== "number" || typeof r.endChar !== "number" || typeof r.text !== "string") return null;
    let refs: string[] = [];
    if (r.refs !== undefined && r.refs !== null) {
      if (!Array.isArray(r.refs) || !r.refs.every((x) => typeof x === "string")) return null;
      refs = r.refs as string[];
    }
    let linkFailed = false;
    if (r.linkFailed !== undefined) {
      if (typeof r.linkFailed !== "boolean") return null;
      linkFailed = r.linkFailed;
    }
    results.push({ startChar: r.startChar, endChar: r.endChar, text: r.text, refs, linkFailed });
  }
  const refData: Record<string, { heRef: string; primaryCategory: string }> = {};
  for (const [k, v] of Object.entries(refDataRaw)) {
    if (!isObj(v) || typeof v.heRef !== "string" || typeof v.primaryCategory !== "string") return null;
    refData[k] = { heRef: v.heRef, primaryCategory: v.primaryCategory };
  }
  return { results, refData };
}

// Resolve citations in `body` (plain text, exactly as the caller will map
// offsets against). cacheKey is the caller's identity for this text (e.g. the
// source ref) - results AND failures are cached for the session, so each
// opened source costs at most one POST + a short poll, ever.
export async function findRefsInText(cacheKey: string, body: string): Promise<FindRefsResult | null> {
  const hit = findRefsCache.get(cacheKey);
  if (hit !== undefined) return hit;
  const inflight = findRefsInflight.get(cacheKey);
  if (inflight) return inflight; // single-flight: join the request already running
  if (findRefsSpent >= FINDREFS_SESSION_CAP) {
    findRefsCache.set(cacheKey, null);
    return null;
  }
  const run = async (): Promise<FindRefsResult | null> => {
    const spendOne = (fn: () => Promise<unknown>): Promise<unknown> => {
      if (findRefsSpent >= FINDREFS_SESSION_CAP) {
        throw new SefariaError(`find-refs request budget (${FINDREFS_SESSION_CAP}) exhausted this session`, 0);
      }
      findRefsSpent++;
      return oneShot(fn);
    };
    let out: FindRefsResult | null = null;
    try {
      const posted = await spendOne(() => postJson("/find-refs", { text: { title: "", body }, lang: "en" }));
      const taskId = isObj(posted) && typeof posted.task_id === "string" ? posted.task_id : null;
      if (taskId) {
        for (const ms of FINDREFS_POLL_MS) {
          await sleep(ms);
          const polled = await spendOne(() => fetchJson(`/async/${encodeURIComponent(taskId)}`));
          if (!isObj(polled)) break; // drift -> null
          if (polled.ready !== true) continue; // still queued -> keep polling
          out = parseFindRefs(polled);
          break;
        }
      }
    } catch {
      out = null; // enhancement: never throws, never retries
    }
    findRefsCache.set(cacheKey, out);
    return out;
  };
  const p = run();
  findRefsInflight.set(cacheKey, p);
  try {
    return await p;
  } finally {
    findRefsInflight.delete(cacheKey);
  }
}

// ---- text search (verbatim quote resolution) --------------------------------
// Sefaria's citation linker (find-refs) resolves NAMED citations ("as in Numbers
// 19") but misses verses a commentary embeds VERBATIM without naming them - the
// gap that makes a discursive commentary (Abarbanel, Akeidat Yitzchak...) look
// like a dead-end. The search index DOES resolve them: an exact-phrase query
// returns the passages whose text contains the phrase. This is the retrieval
// half only; the caller PROVES each match by exact-containment against getText
// (quoteResolver.ts) before asserting any link - search rank alone is never a
// citation (accuracy-lock: we never invent a mareh makom). Cached + capped like
// find-refs, on its own allowance so it can never drain core reading.
const searchCache = new Map<string, string[]>();
const searchInflight = new Map<string, Promise<string[]>>();
let searchSpent = 0;
// Quote-recovery now runs for any source with no detected in-text citation (not
// just pure dead-ends), so the allowance is higher - but still hard-bounded, and
// cue-less sources spend zero (extractQuotedSpans returns []). Cached per phrase.
const SEARCH_SESSION_CAP = 150;

// The canonical ref out of a search-hit _id ("Numbers 19:10 (Version Title [he])").
// The version annotation is always a trailing " (... [xx])"; strip it. The ref is
// re-validated downstream (getText + exact-containment), so a parse quirk that
// yields a bad ref simply fails verification and is dropped - never linked.
function refFromHitId(id: string): string | null {
  const ref = id.replace(/\s*\([^()]*\[[a-z]{2}\]\)\s*$/i, "").trim();
  return ref || null;
}

function parseSearchHits(d: unknown): string[] | null {
  if (!isObj(d)) return null;
  const outer = isObj(d.hits) ? d.hits.hits : undefined;
  if (!Array.isArray(outer)) return null;
  const refs: string[] = [];
  for (const h of outer) {
    if (!isObj(h) || typeof h._id !== "string") return null; // shape drift -> skip whole result
    const ref = refFromHitId(h._id);
    if (ref) refs.push(ref);
  }
  return refs;
}

// Resolve an exact Hebrew phrase to candidate refs (best-ranked first). Returns
// [] on any drift/error/cap - a reading enhancement never throws or retries.
export async function searchRefs(phrase: string, size = 5): Promise<string[]> {
  const hit = searchCache.get(phrase);
  if (hit !== undefined) return hit;
  const inflight = searchInflight.get(phrase);
  if (inflight) return inflight;
  if (searchSpent >= SEARCH_SESSION_CAP) {
    searchCache.set(phrase, []);
    return [];
  }
  const run = async (): Promise<string[]> => {
    let out: string[] = [];
    try {
      searchSpent++;
      const posted = await oneShot(() =>
        // simple:true -> no CORS preflight (search-wrapper does not answer OPTIONS)
        postJson("/search-wrapper", { query: phrase, type: "text", field: "exact", size }, { simple: true }),
      );
      out = parseSearchHits(posted) ?? [];
    } catch {
      out = [];
    }
    searchCache.set(phrase, out);
    return out;
  };
  const p = run();
  searchInflight.set(phrase, p);
  try {
    return await p;
  } finally {
    searchInflight.delete(phrase);
  }
}

// Test/diagnostic hook (not for UI): how many requests we've spent this session.
export function requestsSpent(): number {
  return spent;
}

// Test-only: clear the per-session text/links caches so each test starts clean.
export function _resetCaches(): void {
  textCache.clear();
  linksCache.clear();
  linksInflight.clear();
  findRefsCache.clear();
  findRefsInflight.clear();
  findRefsSpent = 0;
  searchCache.clear();
  searchInflight.clear();
  searchSpent = 0;
  textInflight.clear();
  recoverTextSpent = 0;
}
